import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Target, 
  ArrowUpRight, 
  ArrowDownRight,
  RefreshCw,
  FileSpreadsheet,
  BarChart3,
  Calendar,
  Stethoscope,
  Trophy,
  Medal,
  Award,
  Crown,
  XCircle,
  CalendarRange,
  CalendarCheck
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function Dashboard() {
  const { getAuthHeader, isAdmin } = useAuth();
  const [performanceData, setPerformanceData] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [growthData, setGrowthData] = useState(null);
  const [medicaSimpleData, setMedicaSimpleData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [selectedUser, setSelectedUser] = useState('all');
  const [dateType, setDateType] = useState('ticket'); // 'ticket' or 'treatment'
  const [loading, setLoading] = useState(true);
  const [exchangeRates, setExchangeRates] = useState({ usd: 35, eur: 38, gbp: 45 });
  
  // Staff can only see current month
  const getCurrentMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0]
    };
  };
  
  // Applied date range (used for API calls)
  const [appliedStartDate, setAppliedStartDate] = useState(() => {
    if (!isAdmin()) {
      return getCurrentMonthRange().start;
    }
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [appliedEndDate, setAppliedEndDate] = useState(() => {
    if (!isAdmin()) {
      return getCurrentMonthRange().end;
    }
    return new Date().toISOString().split('T')[0];
  });
  
  // Temporary date range (for picker UI)
  const [tempStartDate, setTempStartDate] = useState(() => {
    if (!isAdmin()) {
      return getCurrentMonthRange().start;
    }
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [tempEndDate, setTempEndDate] = useState(() => {
    if (!isAdmin()) {
      return getCurrentMonthRange().end;
    }
    return new Date().toISOString().split('T')[0];
  });
  
  const [rankingMonth, setRankingMonth] = useState('all'); // For ranking filter

  // Apply date filter
  const applyDateFilter = () => {
    // Staff cannot change dates
    if (!isAdmin()) return;
    setAppliedStartDate(tempStartDate);
    setAppliedEndDate(tempEndDate);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API_URL}/api/performance?date_type=${dateType}`, getAuthHeader()),
        axios.get(`${API_URL}/api/rankings/monthly`, getAuthHeader()),
        axios.get(`${API_URL}/api/performance/growth?start_date=${appliedStartDate}&end_date=${appliedEndDate}&date_type=${dateType}`, getAuthHeader()),
        axios.get(`${API_URL}/api/dashboard/summary`, getAuthHeader())
      ];
      
      const [perfResponse, rankResponse, growthResponse, summaryResponse] = await Promise.all(requests);
      setPerformanceData(perfResponse.data);
      setRankings(rankResponse.data);
      setGrowthData(growthResponse.data);
      setSummaryData(summaryResponse.data);
      if (perfResponse.data.length > 0 && perfResponse.data[0].exchange_rates) {
        setExchangeRates(perfResponse.data[0].exchange_rates);
      }
      
      // Fetch Medica Simple data separately (admin only - may return 403 for staff)
      try {
        const medicaSimpleResponse = await axios.get(
          `${API_URL}/api/performance/medica-simple?start_date=${appliedStartDate}&end_date=${appliedEndDate}`, 
          getAuthHeader()
        );
        setMedicaSimpleData(medicaSimpleResponse.data);
      } catch {
        // Staff users will get 403 - this is expected
        setMedicaSimpleData(null);
      }
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, dateType, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value, currency = 'TRY') => {
    const currencyMap = { TRY: 'TRY', USD: 'USD', EUR: 'EUR', GBP: 'GBP' };
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyMap[currency] || 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => `%${value.toFixed(1)}`;

  // Filter data based on user selection
  const userFilteredData = selectedUser === 'all' 
    ? performanceData 
    : performanceData.filter(d => d.user_email === selectedUser);

  // Filter by date range - filter monthly_data inside each user
  const filteredData = userFilteredData.map(user => {
    const filteredMonthlyData = (user.monthly_data || []).filter(m => {
      const monthKey = m.month || '';
      // Month format is YYYY-MM, convert to date range check
      if (!monthKey || monthKey === 'Bilinmeyen') return true;
      
      const [year, month] = monthKey.split('-');
      if (!year || !month) return true;
      
      const monthStart = `${year}-${month}-01`;
      const monthEnd = `${year}-${month}-31`;
      
      // Check if month overlaps with selected date range
      return monthEnd >= appliedStartDate && monthStart <= appliedEndDate;
    });
    
    return {
      ...user,
      monthly_data: filteredMonthlyData
    };
  });

  // Calculate totals from filtered monthly data
  const totals = filteredData.reduce((acc, user) => {
    (user.monthly_data || []).forEach(month => {
      acc.leads += month.leads || 0;
      acc.sales += month.sales_count || 0;
      acc.revenue += month.total_revenue_try || 0;
      acc.expenses += month.total_expenses || 0;
      
      // Sales info
      const si = month.sales_info || {};
      acc.salesInfo.count += si.count || 0;
      acc.salesInfo.revenue_try += si.revenue_try || 0;
      acc.salesInfo.revenue_usd += si.revenue_usd || 0;
      acc.salesInfo.revenue_eur += si.revenue_eur || 0;
      acc.salesInfo.revenue_gbp += si.revenue_gbp || 0;

      // Reference info
      const ri = month.reference_info || {};
      acc.referenceInfo.count += ri.count || 0;
      acc.referenceInfo.revenue_try += ri.revenue_try || 0;
      acc.referenceInfo.revenue_usd += ri.revenue_usd || 0;
      acc.referenceInfo.revenue_eur += ri.revenue_eur || 0;
      acc.referenceInfo.revenue_gbp += ri.revenue_gbp || 0;

      // ELSA Frost info
      const ei = month.elsa_frost_info || {};
      acc.elsaFrostInfo.count += ei.count || 0;
      acc.elsaFrostInfo.revenue_try += ei.revenue_try || 0;
      acc.elsaFrostInfo.revenue_usd += ei.revenue_usd || 0;
      acc.elsaFrostInfo.revenue_eur += ei.revenue_eur || 0;
      acc.elsaFrostInfo.revenue_gbp += ei.revenue_gbp || 0;
    });
    
    return acc;
  }, { 
    leads: 0, sales: 0, revenue: 0, expenses: 0,
    salesInfo: { count: 0, revenue_try: 0, revenue_usd: 0, revenue_eur: 0, revenue_gbp: 0 },
    referenceInfo: { count: 0, revenue_try: 0, revenue_usd: 0, revenue_eur: 0, revenue_gbp: 0 },
    elsaFrostInfo: { count: 0, revenue_try: 0, revenue_usd: 0, revenue_eur: 0, revenue_gbp: 0 }
  });

  totals.netProfit = totals.revenue - totals.expenses;
  totals.conversionRate = totals.leads > 0 ? (totals.sales / totals.leads * 100) : 0;
  totals.costPerLead = totals.leads > 0 ? totals.expenses / totals.leads : 0;

  // Calculate cancelled totals
  const cancelledTotals = filteredData.reduce((acc, user) => {
    const c = user.cancelled || {};
    acc.count += c.count || 0;
    acc.lost_try += c.lost_try || 0;
    acc.lost_usd += c.lost_usd || 0;
    acc.lost_eur += c.lost_eur || 0;
    acc.lost_gbp += c.lost_gbp || 0;
    acc.lost_revenue_try += c.lost_revenue_try || 0;
    // Merge by_type
    Object.entries(c.by_type || {}).forEach(([type, data]) => {
      if (!acc.by_type[type]) acc.by_type[type] = { count: 0 };
      acc.by_type[type].count += data.count || 0;
    });
    return acc;
  }, { count: 0, lost_try: 0, lost_usd: 0, lost_eur: 0, lost_gbp: 0, lost_revenue_try: 0, by_type: {} });

  // Prepare chart data
  const monthlyChartData = [];
  
  filteredData.forEach(user => {
    user.monthly_data?.forEach(month => {
      const existing = monthlyChartData.find(m => m.month === month.month);
      if (existing) {
        existing.revenue += month.total_revenue_try || 0;
        existing.expenses += month.total_expenses || 0;
        existing.sales += month.sales_count || 0;
        existing.leads += month.leads || 0;
      } else {
        monthlyChartData.push({
          month: month.month,
          revenue: month.total_revenue_try || 0,
          expenses: month.total_expenses || 0,
          sales: month.sales_count || 0,
          leads: month.leads || 0
        });
      }
    });
  });

  monthlyChartData.sort((a, b) => a.month.localeCompare(b.month));

  // Pie chart data for Sales vs Reference vs Elsa Frost
  const pieChartData = [
    { name: 'Satış', value: totals.salesInfo.count, color: '#3b82f6' },
    { name: 'Referans', value: totals.referenceInfo.count, color: '#8b5cf6' },
    { name: 'Elsa Frost', value: totals.elsaFrostInfo.count, color: '#10b981' }
  ].filter(d => d.value > 0);

  // Filter rankings based on selected month
  const filteredRankings = rankingMonth === 'all' 
    ? rankings 
    : rankings.filter(r => r.month_key === rankingMonth);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="dashboard-loading">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold font-[Manrope] tracking-tight">Performans Paneli</h2>
          <p className="text-muted-foreground">Önceki yıla göre büyüme karşılaştırması</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {/* Date Type Toggle */}
          <Tabs value={dateType} onValueChange={setDateType} className="w-auto">
            <TabsList>
              <TabsTrigger value="ticket" className="flex items-center gap-1" data-testid="date-type-ticket">
                <Calendar className="h-3 w-3" />
                Bilet Tarihi
              </TabsTrigger>
              <TabsTrigger value="treatment" className="flex items-center gap-1" data-testid="date-type-treatment">
                <Stethoscope className="h-3 w-3" />
                Tedavi Tarihi
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {isAdmin() && (
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="w-[180px]" data-testid="user-filter">
                <SelectValue placeholder="Kullanıcı seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
                {performanceData.map(user => (
                  <SelectItem key={user.user_email} value={user.user_email}>
                    {user.user_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Date Range Picker - Staff için sadece mevcut ay görünür */}
          {isAdmin() ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[200px] justify-start" data-testid="date-range-btn">
                  <CalendarRange className="h-4 w-4 mr-2" />
                  {new Date(appliedStartDate).toLocaleDateString('tr-TR')} - {new Date(appliedEndDate).toLocaleDateString('tr-TR')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="end">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Başlangıç Tarihi</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={tempStartDate}
                      onChange={(e) => setTempStartDate(e.target.value)}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">Bitiş Tarihi</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={tempEndDate}
                      onChange={(e) => setTempEndDate(e.target.value)}
                      data-testid="end-date-input"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const today = new Date();
                        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                        setTempStartDate(firstDay.toISOString().split('T')[0]);
                        setTempEndDate(today.toISOString().split('T')[0]);
                      }}
                    >
                      Bu Ay
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const today = new Date();
                        const firstDay = new Date(today.getFullYear(), 0, 1);
                        setTempStartDate(firstDay.toISOString().split('T')[0]);
                        setTempEndDate(today.toISOString().split('T')[0]);
                      }}
                    >
                      Bu Yıl
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const today = new Date();
                        const lastYear = new Date(today.getFullYear() - 1, 0, 1);
                        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
                        setTempStartDate(lastYear.toISOString().split('T')[0]);
                        setTempEndDate(lastYearEnd.toISOString().split('T')[0]);
                      }}
                    >
                      Geçen Yıl
                    </Button>
                  </div>
                  <Button 
                    className="w-full"
                    onClick={applyDateFilter}
                    data-testid="apply-date-filter-btn"
                  >
                    Filtrele
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            /* Staff için tarih seçici kilitli - sadece mevcut ay */
            <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-muted/50" data-testid="staff-date-locked">
              <CalendarRange className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {new Date().toLocaleString('tr-TR', { month: 'long', year: 'numeric' })} (Kilitli)
              </span>
            </div>
          )}
          
          <Button variant="outline" onClick={fetchData} className="btn-scale" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Özet Kartları */}
      {summaryData && (
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {/* Bekleyen 2. Visit */}
          <motion.div variants={itemVariants}>
            <Card className="border-l-4 border-l-cyan-500" data-testid="summary-pending-visits">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Bekleyen 2. Visit</p>
                    <p className="text-2xl font-bold text-cyan-600">{summaryData.second_visit?.pending || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                    <CalendarCheck className="h-6 w-6 text-cyan-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* 3+ Ay Gecikmeli */}
          <motion.div variants={itemVariants}>
            <Card className="border-l-4 border-l-red-500" data-testid="summary-overdue-visits">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">3+ Ay Gecikmeli</p>
                    <p className="text-2xl font-bold text-red-600">{summaryData.second_visit?.overdue_3_months || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                {summaryData.second_visit?.overdue_3_months > 0 && (
                  <p className="text-xs text-red-500 mt-2">Acil ilgilenilmeli!</p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Bu Ay Satış */}
          <motion.div variants={itemVariants}>
            <Card className="border-l-4 border-l-green-500" data-testid="summary-monthly-sales">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{summaryData.current_month?.month_name} Satış</p>
                    <p className="text-2xl font-bold text-green-600">{summaryData.current_month?.sales_count || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                {isAdmin() && summaryData.current_month?.revenue_try > 0 && (
                  <p className="text-xs text-green-600 mt-2">
                    ₺{Math.round(summaryData.current_month.revenue_try).toLocaleString('tr-TR')}
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Bu Ay İptal */}
          <motion.div variants={itemVariants}>
            <Card className="border-l-4 border-l-amber-500" data-testid="summary-monthly-cancelled">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{summaryData.current_month?.month_name} İptal</p>
                    <p className="text-2xl font-bold text-amber-600">{summaryData.current_month?.cancelled_count || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <XCircle className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      )}

      {performanceData.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <FileSpreadsheet className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Veri Bulunamadı</h3>
            <p className="text-muted-foreground">
              {isAdmin() 
                ? 'Google Sheets verisi yüklemek için Entegrasyonlar sayfasını kullanın' 
                : 'Henüz performans verisi bulunmuyor'}
            </p>
          </div>
        </Card>
      ) : (
        <>
          {/* Growth Card - Büyüme Göstergesi */}
          {growthData && !growthData.error && (
            <Card className="border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="growth-card">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  BÜYÜME - Önceki Yıl Karşılaştırması
                </CardTitle>
                <CardDescription>
                  {new Date(appliedStartDate).toLocaleDateString('tr-TR')} - {new Date(appliedEndDate).toLocaleDateString('tr-TR')} vs {new Date(growthData.previous_period?.start_date).toLocaleDateString('tr-TR')} - {new Date(growthData.previous_period?.end_date).toLocaleDateString('tr-TR')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Satış Büyümesi */}
                  <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300">SATIŞ Büyümesi</span>
                      {growthData.growth?.sales?.percentage >= 0 ? (
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-3xl font-bold ${growthData.growth?.sales?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {growthData.growth?.sales?.percentage >= 0 ? '+' : ''}{growthData.growth?.sales?.percentage}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Adet Farkı:</span>
                        <span className={`font-semibold ${growthData.growth?.sales?.count_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {growthData.growth?.sales?.count_diff >= 0 ? '+' : ''}{growthData.growth?.sales?.count_diff}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs border-t pt-2">
                        <span className="text-muted-foreground">Bu Dönem:</span>
                        <span className="font-medium">{growthData.current_period?.sales_count}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Önceki Yıl:</span>
                        <span className="font-medium">{growthData.previous_period?.sales_count}</span>
                      </div>
                    </div>
                  </div>

                  {/* Referans Büyümesi */}
                  <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-700 dark:text-purple-300">REFERANS Büyümesi</span>
                      {growthData.growth?.reference?.percentage >= 0 ? (
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-3xl font-bold ${growthData.growth?.reference?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {growthData.growth?.reference?.percentage >= 0 ? '+' : ''}{growthData.growth?.reference?.percentage}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Adet Farkı:</span>
                        <span className={`font-semibold ${growthData.growth?.reference?.count_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {growthData.growth?.reference?.count_diff >= 0 ? '+' : ''}{growthData.growth?.reference?.count_diff}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs border-t pt-2">
                        <span className="text-muted-foreground">Bu Dönem:</span>
                        <span className="font-medium">{growthData.current_period?.reference_count}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Önceki Yıl:</span>
                        <span className="font-medium">{growthData.previous_period?.reference_count}</span>
                      </div>
                    </div>
                  </div>

                  {/* ELSA Büyümesi */}
                  <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">ELSA Büyümesi</span>
                      {growthData.growth?.elsa?.percentage >= 0 ? (
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-3xl font-bold ${growthData.growth?.elsa?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {growthData.growth?.elsa?.percentage >= 0 ? '+' : ''}{growthData.growth?.elsa?.percentage}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Adet Farkı:</span>
                        <span className={`font-semibold ${growthData.growth?.elsa?.count_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {growthData.growth?.elsa?.count_diff >= 0 ? '+' : ''}{growthData.growth?.elsa?.count_diff}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs border-t pt-2">
                        <span className="text-muted-foreground">Bu Dönem:</span>
                        <span className="font-medium">{growthData.current_period?.elsa_count}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Önceki Yıl:</span>
                        <span className="font-medium">{growthData.previous_period?.elsa_count}</span>
                      </div>
                    </div>
                  </div>

                  {/* Toplam Büyüme */}
                  <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300">TOPLAM Büyüme</span>
                      {growthData.growth?.total?.percentage >= 0 ? (
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-3xl font-bold ${growthData.growth?.total?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {growthData.growth?.total?.percentage >= 0 ? '+' : ''}{growthData.growth?.total?.percentage}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Adet Farkı:</span>
                        <span className={`font-semibold ${growthData.growth?.total?.count_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {growthData.growth?.total?.count_diff >= 0 ? '+' : ''}{growthData.growth?.total?.count_diff}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs border-t pt-2">
                        <span className="text-muted-foreground">Bu Dönem:</span>
                        <span className="font-medium">{growthData.current_period?.total_count}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Önceki Yıl:</span>
                        <span className="font-medium">{growthData.previous_period?.total_count}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ciro Büyümesi - Sadece Admin */}
                {isAdmin() && (
                  <div className="mt-6 space-y-4">
                    {/* TL Ciro Büyümesi */}
                    <div className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border border-green-200 dark:border-green-800">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <span className="text-sm font-medium text-green-700 dark:text-green-300">Ciro Büyümesi (TL Toplam)</span>
                          <div className="flex items-baseline gap-3 mt-1">
                            <span className={`text-4xl font-bold ${growthData.growth?.revenue?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {growthData.growth?.revenue?.percentage >= 0 ? '+' : ''}{growthData.growth?.revenue?.percentage}%
                            </span>
                            <span className={`text-lg ${growthData.growth?.revenue?.amount_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ({growthData.growth?.revenue?.amount_diff >= 0 ? '+' : ''}₺{Math.round(growthData.growth?.revenue?.amount_diff || 0).toLocaleString('tr-TR')})
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-6 text-sm">
                          <div className="text-center">
                            <p className="text-muted-foreground">Bu Dönem</p>
                            <p className="font-bold text-lg">₺{Math.round(growthData.current_period?.revenue_try || 0).toLocaleString('tr-TR')}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-muted-foreground">Önceki Yıl</p>
                            <p className="font-bold text-lg">₺{Math.round(growthData.previous_period?.revenue_try || 0).toLocaleString('tr-TR')}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Döviz Bazlı Ciro Büyümeleri */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* USD Büyümesi */}
                      <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">USD Ciro</span>
                          {growthData.growth?.revenue_usd?.percentage >= 0 ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-2xl font-bold ${growthData.growth?.revenue_usd?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {growthData.growth?.revenue_usd?.percentage >= 0 ? '+' : ''}{growthData.growth?.revenue_usd?.percentage || 0}%
                          </span>
                        </div>
                        <div className="mt-2 text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Bu Dönem:</span>
                            <span className="font-medium">${Math.round(growthData.current_period?.revenue_usd || 0).toLocaleString('tr-TR')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Önceki Yıl:</span>
                            <span className="font-medium">${Math.round(growthData.previous_period?.revenue_usd || 0).toLocaleString('tr-TR')}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1">
                            <span className="text-muted-foreground">Fark:</span>
                            <span className={`font-semibold ${growthData.growth?.revenue_usd?.amount_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {growthData.growth?.revenue_usd?.amount_diff >= 0 ? '+' : ''}${Math.round(growthData.growth?.revenue_usd?.amount_diff || 0).toLocaleString('tr-TR')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* EUR Büyümesi */}
                      <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-purple-700 dark:text-purple-300">EUR Ciro</span>
                          {growthData.growth?.revenue_eur?.percentage >= 0 ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-2xl font-bold ${growthData.growth?.revenue_eur?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {growthData.growth?.revenue_eur?.percentage >= 0 ? '+' : ''}{growthData.growth?.revenue_eur?.percentage || 0}%
                          </span>
                        </div>
                        <div className="mt-2 text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Bu Dönem:</span>
                            <span className="font-medium">€{Math.round(growthData.current_period?.revenue_eur || 0).toLocaleString('tr-TR')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Önceki Yıl:</span>
                            <span className="font-medium">€{Math.round(growthData.previous_period?.revenue_eur || 0).toLocaleString('tr-TR')}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1">
                            <span className="text-muted-foreground">Fark:</span>
                            <span className={`font-semibold ${growthData.growth?.revenue_eur?.amount_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {growthData.growth?.revenue_eur?.amount_diff >= 0 ? '+' : ''}€{Math.round(growthData.growth?.revenue_eur?.amount_diff || 0).toLocaleString('tr-TR')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* GBP Büyümesi */}
                      <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">GBP Ciro</span>
                          {growthData.growth?.revenue_gbp?.percentage >= 0 ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-2xl font-bold ${growthData.growth?.revenue_gbp?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {growthData.growth?.revenue_gbp?.percentage >= 0 ? '+' : ''}{growthData.growth?.revenue_gbp?.percentage || 0}%
                          </span>
                        </div>
                        <div className="mt-2 text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Bu Dönem:</span>
                            <span className="font-medium">£{Math.round(growthData.current_period?.revenue_gbp || 0).toLocaleString('tr-TR')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Önceki Yıl:</span>
                            <span className="font-medium">£{Math.round(growthData.previous_period?.revenue_gbp || 0).toLocaleString('tr-TR')}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1">
                            <span className="text-muted-foreground">Fark:</span>
                            <span className={`font-semibold ${growthData.growth?.revenue_gbp?.amount_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {growthData.growth?.revenue_gbp?.amount_diff >= 0 ? '+' : ''}£{Math.round(growthData.growth?.revenue_gbp?.amount_diff || 0).toLocaleString('tr-TR')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Ciro Büyümesi Medica Simple - Sadece Admin */}
          {isAdmin() && medicaSimpleData && !medicaSimpleData.error && (
            <Card data-testid="medica-simple-growth-card" className="border-2 border-teal-200 dark:border-teal-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2 text-teal-700 dark:text-teal-300">
                  <TrendingUp className="h-5 w-5" />
                  Ciro Büyümesi Medica Simple
                </CardTitle>
                <CardDescription>
                  Gerçek ödeme kayıtlarına dayalı ciro analizi
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* TL Karşılığı Toplam Büyümesi */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-950/30 dark:to-cyan-950/30 border border-teal-200 dark:border-teal-800 mb-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <span className="text-sm font-medium text-teal-700 dark:text-teal-300">TL Karşılığı Toplam</span>
                      <div className="flex items-baseline gap-3 mt-1">
                        <span className={`text-4xl font-bold ${medicaSimpleData.growth?.tl_karsiligi?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {medicaSimpleData.growth?.tl_karsiligi?.percentage >= 0 ? '+' : ''}{medicaSimpleData.growth?.tl_karsiligi?.percentage}%
                        </span>
                        <span className={`text-lg ${medicaSimpleData.growth?.tl_karsiligi?.amount_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ({medicaSimpleData.growth?.tl_karsiligi?.amount_diff >= 0 ? '+' : ''}₺{Math.round(medicaSimpleData.growth?.tl_karsiligi?.amount_diff || 0).toLocaleString('tr-TR')})
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-6 text-sm">
                      <div className="text-center">
                        <p className="text-muted-foreground">Bu Dönem</p>
                        <p className="font-bold text-lg">₺{Math.round(medicaSimpleData.current_period?.tl_karsiligi || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-muted-foreground">Önceki Yıl</p>
                        <p className="font-bold text-lg">₺{Math.round(medicaSimpleData.previous_period?.tl_karsiligi || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* İşlem Sayısı */}
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">İşlem Sayısı</span>
                      <div className="flex items-baseline gap-3 mt-1">
                        <span className={`text-2xl font-bold ${medicaSimpleData.growth?.islem_sayisi?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {medicaSimpleData.growth?.islem_sayisi?.percentage >= 0 ? '+' : ''}{medicaSimpleData.growth?.islem_sayisi?.percentage}%
                        </span>
                        <span className={`text-sm ${medicaSimpleData.growth?.islem_sayisi?.count_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ({medicaSimpleData.growth?.islem_sayisi?.count_diff >= 0 ? '+' : ''}{medicaSimpleData.growth?.islem_sayisi?.count_diff})
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-muted-foreground text-xs">Bu Dönem</p>
                        <p className="font-bold">{medicaSimpleData.current_period?.islem_sayisi || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-muted-foreground text-xs">Önceki Yıl</p>
                        <p className="font-bold">{medicaSimpleData.previous_period?.islem_sayisi || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Döviz Bazlı Ciro Büyümeleri */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* TL */}
                  <div className="p-3 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-green-700 dark:text-green-300">TL</span>
                      {medicaSimpleData.growth?.tl?.percentage >= 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-xl font-bold ${medicaSimpleData.growth?.tl?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {medicaSimpleData.growth?.tl?.percentage >= 0 ? '+' : ''}{medicaSimpleData.growth?.tl?.percentage || 0}%
                      </span>
                    </div>
                    <div className="mt-1 text-xs space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bu:</span>
                        <span className="font-medium">₺{Math.round(medicaSimpleData.current_period?.tl || 0).toLocaleString('tr-TR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Önc:</span>
                        <span className="font-medium">₺{Math.round(medicaSimpleData.previous_period?.tl || 0).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>

                  {/* GBP */}
                  <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-300">GBP</span>
                      {medicaSimpleData.growth?.gbp?.percentage >= 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-xl font-bold ${medicaSimpleData.growth?.gbp?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {medicaSimpleData.growth?.gbp?.percentage >= 0 ? '+' : ''}{medicaSimpleData.growth?.gbp?.percentage || 0}%
                      </span>
                    </div>
                    <div className="mt-1 text-xs space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bu:</span>
                        <span className="font-medium">£{Math.round(medicaSimpleData.current_period?.gbp || 0).toLocaleString('tr-TR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Önc:</span>
                        <span className="font-medium">£{Math.round(medicaSimpleData.previous_period?.gbp || 0).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>

                  {/* EUR */}
                  <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-purple-700 dark:text-purple-300">EUR</span>
                      {medicaSimpleData.growth?.eur?.percentage >= 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-xl font-bold ${medicaSimpleData.growth?.eur?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {medicaSimpleData.growth?.eur?.percentage >= 0 ? '+' : ''}{medicaSimpleData.growth?.eur?.percentage || 0}%
                      </span>
                    </div>
                    <div className="mt-1 text-xs space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bu:</span>
                        <span className="font-medium">€{Math.round(medicaSimpleData.current_period?.eur || 0).toLocaleString('tr-TR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Önc:</span>
                        <span className="font-medium">€{Math.round(medicaSimpleData.previous_period?.eur || 0).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>

                  {/* USD */}
                  <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-300">USD</span>
                      {medicaSimpleData.growth?.usd?.percentage >= 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-xl font-bold ${medicaSimpleData.growth?.usd?.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {medicaSimpleData.growth?.usd?.percentage >= 0 ? '+' : ''}{medicaSimpleData.growth?.usd?.percentage || 0}%
                      </span>
                    </div>
                    <div className="mt-1 text-xs space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bu:</span>
                        <span className="font-medium">${Math.round(medicaSimpleData.current_period?.usd || 0).toLocaleString('tr-TR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Önc:</span>
                        <span className="font-medium">${Math.round(medicaSimpleData.previous_period?.usd || 0).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sales Info Cards - Sadece Admin */}
          {isAdmin() && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Satış Bilgisi */}
              <Card data-testid="sales-info-card">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-blue-500" />
                  Satış Bilgisi
                </CardTitle>
                <CardDescription>SATIŞ + SATIŞ 2. VİSİT</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Toplam Adet</span>
                    <span className="text-2xl font-bold">{totals.salesInfo.count}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t">
                    <div>
                      <p className="text-xs text-muted-foreground">TL</p>
                      <p className="font-semibold text-green-600">{formatCurrency(totals.salesInfo.revenue_try)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">USD</p>
                      <p className="font-semibold text-blue-600">{formatCurrency(totals.salesInfo.revenue_usd, 'USD')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">EUR</p>
                      <p className="font-semibold text-purple-600">{formatCurrency(totals.salesInfo.revenue_eur, 'EUR')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">GBP</p>
                      <p className="font-semibold text-amber-600">{formatCurrency(totals.salesInfo.revenue_gbp, 'GBP')}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Referans Satış Bilgisi */}
            <Card data-testid="reference-info-card">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-purple-500" />
                  Referans Satış Bilgisi
                </CardTitle>
                <CardDescription>REFERANS + REFERANS 2. VİSİT</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Toplam Adet</span>
                    <span className="text-2xl font-bold">{totals.referenceInfo.count}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t">
                    <div>
                      <p className="text-xs text-muted-foreground">TL</p>
                      <p className="font-semibold text-green-600">{formatCurrency(totals.referenceInfo.revenue_try)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">USD</p>
                      <p className="font-semibold text-blue-600">{formatCurrency(totals.referenceInfo.revenue_usd, 'USD')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">EUR</p>
                      <p className="font-semibold text-purple-600">{formatCurrency(totals.referenceInfo.revenue_eur, 'EUR')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">GBP</p>
                      <p className="font-semibold text-amber-600">{formatCurrency(totals.referenceInfo.revenue_gbp, 'GBP')}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ELSA FROST Bilgisi */}
            {totals.elsaFrostInfo.count > 0 && (
              <Card data-testid="elsa-frost-info-card" className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="font-[Manrope] flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-emerald-500" />
                    ELSA Bilgisi
                  </CardTitle>
                  <CardDescription>ELSA + ELSA 1. VİSİT + ELSA 2. VİSİT</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Toplam Adet</span>
                      <span className="text-2xl font-bold">{totals.elsaFrostInfo.count}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground">TL</p>
                        <p className="font-semibold text-green-600">{formatCurrency(totals.elsaFrostInfo.revenue_try)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">USD</p>
                        <p className="font-semibold text-blue-600">{formatCurrency(totals.elsaFrostInfo.revenue_usd, 'USD')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">EUR</p>
                        <p className="font-semibold text-purple-600">{formatCurrency(totals.elsaFrostInfo.revenue_eur, 'EUR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">GBP</p>
                        <p className="font-semibold text-amber-600">{formatCurrency(totals.elsaFrostInfo.revenue_gbp, 'GBP')}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          )}

          {/* İptal ve Kayıp Kazanç - Sadece Admin */}
          {isAdmin() && cancelledTotals.count > 0 && (
            <Card className="border-red-300 dark:border-red-900" data-testid="cancelled-performance-card">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" />
                  İptal Edilen Tedaviler - Kayıp Kazanç
                </CardTitle>
                <CardDescription>{cancelledTotals.count} adet tedavi iptal edildi</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                    <p className="text-xs text-red-500">İptal Sayısı</p>
                    <p className="font-bold text-2xl text-red-600">{cancelledTotals.count}</p>
                  </div>
                  {cancelledTotals.lost_try > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                      <p className="text-xs text-red-500">₺ TL Kayıp</p>
                      <p className="font-bold text-lg text-red-600">₺{Math.round(cancelledTotals.lost_try).toLocaleString('tr-TR')}</p>
                    </div>
                  )}
                  {cancelledTotals.lost_usd > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                      <p className="text-xs text-red-500">$ USD Kayıp</p>
                      <p className="font-bold text-lg text-red-600">${Math.round(cancelledTotals.lost_usd).toLocaleString('tr-TR')}</p>
                    </div>
                  )}
                  {cancelledTotals.lost_eur > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                      <p className="text-xs text-red-500">€ EUR Kayıp</p>
                      <p className="font-bold text-lg text-red-600">€{Math.round(cancelledTotals.lost_eur).toLocaleString('tr-TR')}</p>
                    </div>
                  )}
                  {cancelledTotals.lost_gbp > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                      <p className="text-xs text-red-500">£ GBP Kayıp</p>
                      <p className="font-bold text-lg text-red-600">£{Math.round(cancelledTotals.lost_gbp).toLocaleString('tr-TR')}</p>
                    </div>
                  )}
                </div>
                {cancelledTotals.lost_revenue_try > 0 && (
                  <div className="mt-4 p-4 bg-red-100 dark:bg-red-950/30 rounded-lg border border-red-300">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-red-700 dark:text-red-400">Toplam Kayıp Kazanç (TL karşılığı)</span>
                      <span className="text-2xl font-bold text-red-600">₺{Math.round(cancelledTotals.lost_revenue_try).toLocaleString('tr-TR')}</span>
                    </div>
                  </div>
                )}
                {/* İptaller satış türüne göre */}
                {Object.keys(cancelledTotals.by_type).length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {Object.entries(cancelledTotals.by_type).map(([type, data]) => (
                      <Badge key={type} variant="destructive" className="text-xs">
                        {type}: {data.count} adet
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Charts Row - Sadece Admin */}
          {isAdmin() && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Revenue Chart */}
              <Card className="lg:col-span-2" data-testid="revenue-chart">
              <CardHeader>
                <CardTitle className="font-[Manrope]">Gelir & Gider Trendi</CardTitle>
                <CardDescription>Aylık finansal performans ({dateType === 'ticket' ? 'Bilet Tarihine Göre' : 'Tedavi Tarihine Göre'})</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {monthlyChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyChartData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="revenue" name="Gelir" fill="#10b981" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="expenses" name="Gider" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Grafik verisi bulunamadı
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Sales Type Distribution */}
            <Card data-testid="sales-type-chart">
              <CardHeader>
                <CardTitle className="font-[Manrope]">Satış Türü Dağılımı</CardTitle>
                <CardDescription>Satış vs Referans</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {pieChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Veri bulunamadı
                    </div>
                  )}
                </div>
                <div className="flex justify-center gap-4 mt-4">
                  {pieChartData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-muted-foreground">{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          )}

          {/* User Performance Table */}
          {isAdmin() && (
            <Card data-testid="user-performance-table">
              <CardHeader>
                <CardTitle className="font-[Manrope]">Kullanıcı Performansı</CardTitle>
                <CardDescription>Personel bazlı performans özeti</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Kullanıcı</TableHead>
                        <TableHead className="text-right">Lead</TableHead>
                        <TableHead className="text-right">Satış</TableHead>
                        <TableHead className="text-right">Referans</TableHead>
                        <TableHead className="text-right">Dönüşüm</TableHead>
                        <TableHead className="text-right">Gelir (TL)</TableHead>
                        <TableHead className="text-right">Net Kâr</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData.map((user) => (
                        <TableRow key={user.user_email}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{user.user_name}</p>
                              <p className="text-xs text-muted-foreground">{user.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{user.summary?.total_leads || 0}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                              {user.summary?.sales_info?.count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                              {user.summary?.reference_info?.count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline">
                              %{user.summary?.overall_conversion_rate || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-green-600 font-medium">
                            {formatCurrency(user.summary?.total_revenue_try || 0)}
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            <span className={user.summary?.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatCurrency(user.summary?.net_profit || 0)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Exchange Rates Info */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap gap-4 items-center justify-center text-sm text-muted-foreground">
                <span>Döviz Kurları:</span>
                <Badge variant="outline">1 USD = {formatCurrency(exchangeRates.usd)}</Badge>
                <Badge variant="outline">1 EUR = {formatCurrency(exchangeRates.eur)}</Badge>
                <Badge variant="outline">1 GBP = {formatCurrency(exchangeRates.gbp)}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Rankings - Aylık Satış Sıralaması */}
          {isAdmin() && rankings.length > 0 && (
            <Card data-testid="monthly-rankings-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-500" />
                      Aylık Satış Sıralaması
                    </CardTitle>
                    <CardDescription>
                      Tedavi tarihine göre SATIŞ + REFERANS + ELSA FROST toplam sayısı sıralaması
                    </CardDescription>
                  </div>
                  <Select value={rankingMonth} onValueChange={setRankingMonth}>
                    <SelectTrigger className="w-[180px]" data-testid="ranking-month-filter">
                      <SelectValue placeholder="Ay seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Aylar</SelectItem>
                      {rankings.map(r => (
                        <SelectItem key={r.month_key} value={r.month_key}>{r.month_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {filteredRankings.slice(0, rankingMonth === 'all' ? 3 : 1).map((monthData) => (
                    <div key={monthData.month_key} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <h4 className="font-semibold text-lg">{monthData.month_name}</h4>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {monthData.rankings.slice(0, 3).map((user, idx) => {
                          const isFirst = idx === 0;
                          const isSecond = idx === 1;
                          const isThird = idx === 2;
                          
                          const bgColor = isFirst 
                            ? 'bg-gradient-to-br from-yellow-50 to-amber-100 border-yellow-300 dark:from-yellow-900/30 dark:to-amber-900/30 dark:border-yellow-700'
                            : isSecond 
                              ? 'bg-gradient-to-br from-gray-50 to-slate-100 border-gray-300 dark:from-gray-800/50 dark:to-slate-800/50 dark:border-gray-600'
                              : 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200 dark:from-orange-900/20 dark:to-amber-900/20 dark:border-orange-800';
                          
                          const RankIcon = isFirst ? Crown : isSecond ? Medal : Award;
                          const iconColor = isFirst ? 'text-yellow-500' : isSecond ? 'text-gray-400' : 'text-orange-400';
                          
                          return (
                            <motion.div
                              key={user.user_email}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: idx * 0.1 }}
                              className={`p-4 rounded-xl border-2 ${bgColor} relative overflow-hidden`}
                              data-testid={`ranking-${monthData.month_key}-${idx + 1}`}
                            >
                              {/* Rank Badge */}
                              <div className="absolute top-2 right-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  isFirst ? 'bg-yellow-400 text-yellow-900' 
                                    : isSecond ? 'bg-gray-300 text-gray-700' 
                                    : 'bg-orange-300 text-orange-800'
                                }`}>
                                  <span className="font-bold text-sm">{user.rank}</span>
                                </div>
                              </div>
                              
                              {/* Icon */}
                              <div className="flex items-center gap-3 mb-3">
                                <RankIcon className={`h-6 w-6 ${iconColor}`} />
                                <div>
                                  <p className="font-semibold text-sm">{user.user_name}</p>
                                  <p className="text-xs text-muted-foreground truncate max-w-[150px]">{user.user_email}</p>
                                </div>
                              </div>
                              
                              {/* Stats */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-muted-foreground">Toplam Satış</span>
                                  <Badge variant="default" className={isFirst ? 'bg-yellow-500' : isSecond ? 'bg-gray-500' : 'bg-orange-500'}>
                                    {user.total_count}
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <span className="text-blue-600">Satış: {user.sales_count}</span>
                                  <span className="text-purple-600">Referans: {user.referans_count}</span>
                                  {user.elsa_frost_count > 0 && (
                                    <span className="text-emerald-600">ELSA: {user.elsa_frost_count}</span>
                                  )}
                                </div>
                                <div className="pt-2 border-t border-dashed">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-muted-foreground">Ciro (TL)</span>
                                    <span className="text-sm font-semibold text-green-600">
                                      {formatCurrency(user.revenue_try)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                      
                      {/* Other rankings table */}
                      {monthData.rankings.length > 3 && (
                        <div className="mt-2">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-12">Sıra</TableHead>
                                <TableHead>Personel</TableHead>
                                <TableHead className="text-right">Satış</TableHead>
                                <TableHead className="text-right">Referans</TableHead>
                                <TableHead className="text-right">ELSA</TableHead>
                                <TableHead className="text-right">Toplam</TableHead>
                                <TableHead className="text-right">Ciro</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {monthData.rankings.slice(3, 10).map((user) => (
                                <TableRow key={user.user_email}>
                                  <TableCell className="font-medium">{user.rank}.</TableCell>
                                  <TableCell>
                                    <div>
                                      <p className="font-medium text-sm">{user.user_name}</p>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Badge variant="outline" className="text-blue-600">{user.sales_count}</Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Badge variant="outline" className="text-purple-600">{user.referans_count}</Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Badge variant="outline" className="text-emerald-600">{user.elsa_frost_count || 0}</Badge>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{user.total_count}</TableCell>
                                  <TableCell className="text-right text-green-600">
                                    {formatCurrency(user.revenue_try)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
